/**
 * Service Icons
 *
 * Exports reusable SVG icon components for the Services section.
 * Each icon is styled for consistent sizing and color, and can be used inline with text or in cards.
 *
 * @module ServicesIcons
 */

'use client';
import React from 'react';

/**
 * StrategicIcon
 * Renders an SVG icon representing strategic services.
 *
 * @returns {JSX.Element} SVG icon for strategic services
 */
export const StrategicIcon = () => (
  <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 22L10 16H2L4 22H12ZM12 22H16"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 13V12.5C12 10.6144 12 9.67157 11.4142 9.08579C10.8284 8.5 9.88562 8.5 8 8.5C6.11438 8.5 5.17157 8.5 4.58579 9.08579C4 9.67157 4 10.6144 4 12.5V13"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M19 13C19 14.1046 18.1046 15 17 15C15.8954 15 15 14.1046 15 13C15 11.8954 15.8954 11 17 11C18.1046 11 19 11.8954 19 13Z"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M10 4C10 5.10457 9.10457 6 8 6C6.89543 6 6 5.10457 6 4C6 2.89543 6.89543 2 8 2C9.10457 2 10 2.89543 10 4Z"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M14 17.5H20C21.1046 17.5 22 18.3954 22 19.5V20C22 21.1046 21.1046 22 20 22H19"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * DesignIcon
 * Renders an SVG icon representing design services.
 *
 * @returns {JSX.Element} SVG icon for design services
 */
export const DesignIcon = () => (
  <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none">
    <path
      d="M4 8C4 5.17157 4 3.75736 5.00421 2.87868C6.00841 2 7.62465 2 10.8571 2H13.1429C16.3753 2 17.9916 2 18.9958 2.87868C20 3.75736 20 5.17157 20 8V17H4V8Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path d="M3 17H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path
      d="M10.6987 5.56588C11.9289 5.38957 13.9674 5.4601 12.2803 7.15266C10.1715 9.26836 7.00839 14.0289 10.6987 12.4421C14.3891 10.8554 15.9709 11.9132 14.3893 13.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M12 17V21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M5 22L8 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M19 22L16 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

/**
 * DeliveryIcon
 * Renders an SVG icon representing delivery services.
 *
 * @returns {JSX.Element} SVG icon for delivery services
 */
export const DeliveryIcon = () => (
  <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none">
    <path
      d="M20 22V17C20 15.1144 20 14.1716 19.4142 13.5858C18.8284 13 17.8856 13 16 13L12 22L8 13C6.11438 13 5.17157 13 4.58579 13.5858C4 14.1716 4 15.1144 4 17V22"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 15L11.5 19L12 20.5L12.5 19L12 15ZM12 15L11 13H13L12 15Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15.5 6.5V5.5C15.5 3.56701 13.933 2 12 2C10.067 2 8.5 3.56701 8.5 5.5V6.5C8.5 8.433 10.067 9.99998 12 9.99998C13.933 9.99998 15.5 8.433 15.5 6.5Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * CloudIcon
 * Renders an SVG icon representing cloud services.
 *
 * @returns {JSX.Element} SVG icon for cloud services
 */
export const CloudIcon = () => (
  <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none">
    <path
      d="M20.5 16.5V8.5C20.5 6.14298 20.5 4.96447 19.7677 4.23223C19.0355 3.5 17.857 3.5 15.5 3.5H8.5C6.14298 3.5 4.96446 3.5 4.23223 4.23223C3.5 4.96447 3.5 6.14298 3.5 8.5V16.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M21.9842 20.5H2.01579C1.63285 20.5 1.38379 20.1088 1.55505 19.7764L3.5 16.5H20.5L22.4449 19.7764C22.6162 20.1088 22.3671 20.5 21.9842 20.5Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9.5 12.5C8.67157 12.5 8 11.8284 8 11C8 10.1716 8.67157 9.5 9.5 9.5H10C10 8.39543 10.8954 7.5 12 7.5C13.1045 7.5 14 8.39543 14 9.5H14.5C15.3284 9.5 16 10.1716 16 11C16 11.8284 15.3284 12.5 14.5 12.5H9.5Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
